// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronRight from "@store/Library/Components/Toolkit/Icons/ChevronRight";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as cssLinter from "css";
import React, { FC, useMemo, useState } from "react";
import { css } from "styled-components";
import SegmentedToggleComponent, { TabModel } from "@store/Library/Components/SegmentedToggle";

const enum fields {
    state1Label = "state1Label",
    state1Icon = "state1Icon",
    state1LinkText = "state1LinkText",
    state1Link = "state1Link",
    state2Label = "state2Label",
    state2Icon = "state2Icon",
    state2LinkText = "state2LinkText",
    state2Link = "state2Link",
    defaultActiveState = "defaultActiveState",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.state1Label]: string;
        [fields.state1Icon]: string;
        [fields.state1LinkText]: string;
        [fields.state1Link]: LinkFieldValue;
        [fields.state2Label]: string;
        [fields.state2Icon]: string;
        [fields.state2LinkText]: string;
        [fields.state2Link]: LinkFieldValue;
        [fields.defaultActiveState]: "0" | "1";
        [fields.customCSS]: string;
    };
}

export interface SegmentedToggleStyles {
    wrapper?: InjectableCss;
    header?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
    tabPanel?: InjectableCss;
    paneEditLabel?: InjectableCss;
}

export const segmentedToggleStyles: SegmentedToggleStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    header: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            gap: ${pxToRem(12)};
            flex-wrap: nowrap;

            ${breakpoint("sm", "down")} {
                flex-direction: column;
                align-items: flex-end;
            }
        `,
    },
    viewAllLink: linkGetActionLinkProps(ChevronRight, "right"),
    tabPanel: {
        css: css`
            margin-top: ${pxToRem(24)};

            &[data-active="false"][data-editing="false"] {
                display: none;
            }

            &[data-active="false"][data-editing="true"] {
                opacity: 0.5;
                border: ${pxToRem(2)} dashed ${getColor("common.borderLight")};
                border-radius: ${pxToRem(4)};
                margin-top: ${pxToRem(8)};
            }
        `,
    },
    paneEditLabel: {
        css: css`
            padding: ${pxToRem(4)} ${pxToRem(8)};
            font-size: ${pxToRem(12)};
            font-weight: 500;
            color: ${getColor("text.light")};
            background-color: ${getColor("common.accent")};
            border-radius: ${pxToRem(4)} ${pxToRem(4)} 0 0;
        `,
    },
};

const SegmentedToggle: FC<OwnProps> = ({ fields, id }) => {
    const shellContext = useShellContext();
    const [activeIndex, setActiveIndex] = useState<number>(fields.defaultActiveState === "1" ? 1 : 0);

    const state1Link = useGetLink(fields.state1Link);
    const state2Link = useGetLink(fields.state2Link);

    const activeLink = activeIndex === 0 ? state1Link : state2Link;
    const activeLinkText = activeIndex === 0 ? fields.state1LinkText : fields.state2LinkText;

    const tab1Id = `seg-tab-${id}-0`;
    const tab2Id = `seg-tab-${id}-1`;
    const panel1Id = `seg-panel-${id}-0`;
    const panel2Id = `seg-panel-${id}-1`;

    const isEditing = shellContext?.isEditing && shellContext?.isCurrentPage;

    const styles = useMemo(
        () =>
            mergeProps<SegmentedToggleStyles>(segmentedToggleStyles, {
                wrapper: {
                    css: css`
                        ${fields.customCSS}
                    `,
                },
            }),
        [fields.customCSS],
    );

    const tabs: TabModel[] = useMemo(() => {
        return [
            {
                label: fields.state1Label,
                iconUrl: fields.state1Icon,
                clickableProps: {
                    id: tab1Id,
                    "aria-controls": panel1Id,
                },
            },
            {
                label: fields.state2Label,
                iconUrl: fields.state2Icon,
                clickableProps: {
                    id: tab2Id,
                    "aria-controls": panel2Id,
                },
            },
        ];
    }, [
        fields.state1Label,
        fields.state1Icon,
        fields.state2Label,
        fields.state2Icon,
        tab1Id,
        panel1Id,
        tab2Id,
        panel2Id,
    ]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.header}>
                <SegmentedToggleComponent tabs={tabs} onChangeIndex={setActiveIndex} />
                {activeLink.url && (
                    <Link href={activeLink.url} {...styles.viewAllLink}>
                        {activeLinkText || activeLink.title}
                    </Link>
                )}
            </StyledWrapper>
            <StyledWrapper
                {...styles.tabPanel}
                id={panel1Id}
                role="tabpanel"
                aria-labelledby={tab1Id}
                data-active={activeIndex === 0 ? "true" : "false"}
                data-editing={isEditing ? "true" : "false"}
            >
                {isEditing && activeIndex !== 0 && (
                    <StyledWrapper {...styles.paneEditLabel}>
                        {translate("Pane {0} - inactive (toggle to activate)", "1")}
                    </StyledWrapper>
                )}
                <Zone contentId={id} zoneName="Pane0" />
            </StyledWrapper>
            <StyledWrapper
                {...styles.tabPanel}
                id={panel2Id}
                role="tabpanel"
                aria-labelledby={tab2Id}
                data-active={activeIndex === 1 ? "true" : "false"}
                data-editing={isEditing ? "true" : "false"}
            >
                {isEditing && activeIndex !== 1 && (
                    <StyledWrapper {...styles.paneEditLabel}>
                        {translate("Pane {0} - inactive (toggle to activate)", "2")}
                    </StyledWrapper>
                )}
                <Zone contentId={id} zoneName="Pane1" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

/* ─── Widget Module Definition ───────────────────────────────────────── */

const state1Tab = { displayName: "State 1", sortOrder: 0 };
const state2Tab = { displayName: "State 2", sortOrder: 1 };
const advancedTab = { displayName: "Advanced", sortOrder: 2 };

const defaultCustomCss = `& {

}`;

const widgetModule: WidgetModule = {
    component: SegmentedToggle,
    definition: {
        displayName: "Segmented Toggle",
        group: "Common",
        icon: "toggle-on",
        fieldDefinitions: [
            /* ── State 1 ─────────────────────────────────────────────── */
            {
                name: fields.state1Label,
                displayName: "Display Text",
                editorTemplate: "TextField",
                defaultValue: "State 1",
                fieldType: "Translatable",
                sortOrder: 1,
                tab: state1Tab,
                isRequired: true,
            },
            {
                name: fields.state1Icon,
                displayName: "Icon (optional)",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 2,
                tab: state1Tab,
                tooltip:
                    "Upload an SVG icon. The icon color will automatically match the active/inactive state — no need for separate assets per state.",
            },
            {
                name: fields.state1LinkText,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 3,
                tab: state1Tab,
                tooltip:
                    "Label for the link shown when State 1 is active. Falls back to the linked page title if left blank.",
            },
            {
                name: fields.state1Link,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                sortOrder: 4,
                tab: state1Tab,
            },
            /* ── State 2 ─────────────────────────────────────────────── */
            {
                name: fields.state2Label,
                displayName: "Display Text",
                editorTemplate: "TextField",
                defaultValue: "State 2",
                fieldType: "Translatable",
                sortOrder: 1,
                tab: state2Tab,
                isRequired: true,
            },
            {
                name: fields.state2Icon,
                displayName: "Icon (optional)",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 2,
                tab: state2Tab,
                tooltip:
                    "Upload an SVG icon. The icon color will automatically match the active/inactive state — no need for separate assets per state.",
            },
            {
                name: fields.state2LinkText,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 3,
                tab: state2Tab,
                tooltip:
                    "Label for the link shown when State 2 is active. Falls back to the linked page title if left blank.",
            },
            {
                name: fields.state2Link,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                sortOrder: 4,
                tab: state2Tab,
            },
            /* ── Advanced ────────────────────────────────────────────── */
            {
                name: fields.defaultActiveState,
                displayName: "Default Active State",
                editorTemplate: "RadioButtonsField",
                options: [
                    { displayName: "State 1 (first tab)", value: "0" },
                    { displayName: "State 2 (second tab)", value: "1" },
                ],
                defaultValue: "0",
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
                horizontal: true,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                sortOrder: 2,
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (_item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse CSS";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
